"use client";

import dynamic from "next/dynamic";
import { useSession } from "next-auth/react";
import { useEffect, useMemo, useState } from "react";

import type { Service } from "@/types";
import { sanitizeAllowedHtml } from "@/lib/security/sanitize-html";
import { formatServiceDisplayTitle, servicePageTitleWithPrefix } from "@/lib/utils/service-title";
import { LAURA_LISTING_AI_CONTEXT_MAX_CHARS } from "@/lib/constants/laura-listing-context";
import { useServiceCountdownOfferOverlay } from "@/hooks/useServiceCountdownOfferOverlay";
import { isSafeHttpOrHttpsUrl } from "@/lib/security/safe-href";
import { usePageScrollRestore } from "@/lib/hooks/use-page-scroll-restore";
import { extractYouTubeId, getYouTubeEmbedUrl } from "@/lib/utils/youtube";
import { useGetFBTServicesQuery, useGetServiceQuery } from "@/features/services/serviceApiInjection";
import { getDefaultSelectedPackage, storefrontPackagesForService } from "@/lib/logic";

import { Button } from "@/components/ui/button";
import TrackedCtaButton from "@/components/analytics/TrackedCtaButton";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { PlayCircle, X } from "lucide-react";
import Image from "next/image";
import PackageSection from "@/app/services/[...iden]/components/PackageSection";
import ServiceMediaCarousel from "@/app/services/[...iden]/components/ServiceMediaCarousel";
import { ServiceCardSubcategoryPills } from "@/components/cards/ServiceCardSubcategoryPills";
import ServiceReviewSummaryStrip from "@/app/services/[...iden]/components/ServiceReviewSummaryStrip";
import ServiceCountdownBanner from "@/app/services/[...iden]/components/ServiceCountdownBanner";
import ServiceHowItWorksSection from "@/app/services/[...iden]/components/ServiceHowItWorksSection";
import ServicePageEmbedBridge from "@/app/services/[...iden]/components/ServicePageEmbedBridge";
import BookCallDialog from "@/app/services/[...iden]/components/BookCallDialog";
import ServiceDesignPicker, {
	resolveDesignVariants,
} from "@/app/services/[...iden]/components/ServiceDesignPicker";
import ChatNowFAB from "@/app/categories/[categoryId]/components/ChatNowFAB";
import ServiceDeferredBlock, {
	ServiceBlockPlaceholder,
} from "@/app/services/[...iden]/components/ServiceDeferredBlock";

import styles from "./ServiceDetails.module.css";

const DeferredServiceDetailsExpandableDetails = dynamic(
	() => import("@/app/services/[...iden]/components/ServiceDetailsExpandableDetails"),
	{ loading: () => <ServiceBlockPlaceholder className="h-80" /> },
);

const LtoSocialProofBanner = dynamic(
	() => import("@/app/services/[...iden]/components/LtoSocialProofBanner"),
	{ ssr: false },
);

const LtoOfferSubscribePopup = dynamic(
	() => import("@/app/services/[...iden]/components/LtoOfferSubscribePopup"),
	{ ssr: false },
);
const DeferredServiceFrequentlyBoughtTogetherSection = dynamic(
	() => import("@/app/services/[...iden]/components/ServiceFrequentlyBoughtTogetherSection"),
	{ loading: () => <ServiceBlockPlaceholder className="h-72" /> },
);
const DeferredServiceCompanyReviewsCarousel = dynamic(
	() => import("@/app/services/[...iden]/components/ServiceCompanyReviewsCarousel"),
	{ loading: () => <ServiceBlockPlaceholder className="mx-auto h-96 max-w-6xl" /> },
);
const DeferredServiceVideoReviewsCarousel = dynamic(
	() => import("@/app/services/[...iden]/components/ServiceVideoReviewsCarousel"),
	{ loading: () => <ServiceBlockPlaceholder className="mx-auto h-96 max-w-6xl" /> },
);
const DeferredServiceAboutSection = dynamic(
	() => import("@/app/services/[...iden]/components/ServiceAboutSection"),
	{ loading: () => <ServiceBlockPlaceholder className="container h-96" /> },
);

type Props = {
	service: Service;
	frequentlyBoughtTogether?: Service[];
	/** Loaded inside FBT preview iframe (`?embed=1`). */
	embedded?: boolean;
};

function trimLauraGatherBrief(value?: string | null, maxLength = LAURA_LISTING_AI_CONTEXT_MAX_CHARS) {
	if (!value?.trim()) return undefined;
	const trimmed = value.trim();
	return trimmed.length <= maxLength ? trimmed : trimmed.slice(0, maxLength);
}
function toPlainContextText(value?: string | null, maxLength = 3000) {
	if (!value) return "";
	const decode = (input: string) => {
		if (typeof window === "undefined") return input;
		const textarea = document.createElement("textarea");
		textarea.innerHTML = input;
		return textarea.value;
	};
	const htmlStripped =
		typeof window !== "undefined"
			? (new DOMParser().parseFromString(value, "text/html").body.textContent ?? "")
			: value.replace(/<[^>]*>/g, " ");
	return decode(htmlStripped).replace(/\s+/g, " ").trim().slice(0, maxLength);
}

const EMPTY_INLINE_HTML_PATTERN = String.raw`(?:\s|&nbsp;|<br\s*\/?>|<(?:span|strong|em|b|i)(?:\s[^>]*)?>(?:\s|&nbsp;|<br\s*\/?>)*<\/(?:span|strong|em|b|i)>)*`;
const LEADING_EMPTY_DETAILS_HTML_PATTERN = new RegExp(
	String.raw`^(?:\s|&nbsp;|<br\s*\/?>|<(?:p|div)(?:\s[^>]*)?>` +
		EMPTY_INLINE_HTML_PATTERN +
		String.raw`<\/(?:p|div)>)+`,
	"i",
);

function stripLeadingEmptyDetailsHtml(value: string) {
	let next = value.trim();
	let previous = "";
	while (next !== previous) {
		previous = next;
		next = next.replace(LEADING_EMPTY_DETAILS_HTML_PATTERN, "").trimStart();
	}
	return next;
}

function formatAmounts(amounts?: Partial<Record<"USD" | "GBP" | "EUR" | "CAD" | "AUD", number>>) {
	if (!amounts) return "";
	return Object.entries(amounts)
		.filter(([, amount]) => typeof amount === "number")
		.map(([currency, amount]) => `${currency} ${amount}`)
		.join(", ");
}

export default function ServiceDetails({
	service,
	frequentlyBoughtTogether = [],
	embedded = false,
}: Props) {
	const { data: clientSession } = useSession();
	const session = clientSession ?? null;
	usePageScrollRestore(`service-page-scroll:${service._id}`, !embedded);

	const [isMounted, setIsMounted] = useState(false);
	const [bookCallOpen, setBookCallOpen] = useState(false);
	/** null = landing service’s own design (default for ads). */
	const [preferredDesignId, setPreferredDesignId] = useState<string | null>(null);
	const designVariants = useMemo(() => resolveDesignVariants(service), [service]);
	const selectedDesign = useMemo(
		() =>
			preferredDesignId
				? designVariants.find((variant) => variant._id === preferredDesignId) ?? null
				: null,
		[designVariants, preferredDesignId],
	);
	const { data: designFbtData } = useGetFBTServicesQuery(preferredDesignId ?? "", {
		skip: !preferredDesignId,
	});
	const { data: selectedDesignService } = useGetServiceQuery(preferredDesignId ?? "", {
		skip: !preferredDesignId,
	});
	const activeFrequentlyBoughtTogether = preferredDesignId
		? (designFbtData?.serviceIds ?? [])
		: frequentlyBoughtTogether;
	/** Gallery and details follow the picked design. Packages and basket use that design's own service. */
	const displayService = useMemo((): Service => {
		if (!selectedDesign) return service;
		return {
			...service,
			media: selectedDesign.media?.length ? selectedDesign.media : service.media,
			detailsHtml: selectedDesign.detailsHtml ?? service.detailsHtml,
			envatoTemplateLicenseAmounts:
				selectedDesign.envatoTemplateLicenseAmounts ?? service.envatoTemplateLicenseAmounts,
			demoUrl: selectedDesign.demoUrl ?? service.demoUrl,
			thumbnailImage: selectedDesign.thumbnailImage ?? service.thumbnailImage,
			thumbnailDisplayUrl: selectedDesign.thumbnailDisplayUrl ?? service.thumbnailDisplayUrl,
		};
	}, [selectedDesign, service]);
	/** The design the shopper picked is the service added to the basket. */
	const purchaseService =
		preferredDesignId && selectedDesignService?._id === preferredDesignId
			? selectedDesignService
			: displayService;
	const designPurchasePending = Boolean(preferredDesignId) && purchaseService._id === service._id;
	const defaultPackageId = getDefaultSelectedPackage(storefrontPackagesForService(service))?._id ?? "";
	const [selectedPackageIdForTitle, setSelectedPackageIdForTitle] = useState(defaultPackageId);
	const offerOverlay = useServiceCountdownOfferOverlay(service);
	const eyebrowText = service.eyebrowText?.trim();
	const isEyebrowLimitedTimeOffer = offerOverlay?.variant === "active";
	const limitedOfferPillPercent = (() => {
		const raw = service.countdownOffer?.discountPercent;
		if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return undefined;
		return Math.min(100, Math.max(0, Math.round(raw)));
	})();
	const eyebrowPillText = isEyebrowLimitedTimeOffer
		? limitedOfferPillPercent !== undefined
			? `LIMITED TIME OFFER - ${limitedOfferPillPercent}% OFF`
			: "LIMITED TIME OFFER"
		: eyebrowText;
	const showEyebrowPill = Boolean(eyebrowPillText && eyebrowPillText.length > 0);
	const countdownEnabled = service.countdownOffer?.enabled === true;
	// Same visibility window as the countdown banner (active + 2h "OFFER ENDED" grace).
	const isLimitedOfferExperienceActive = offerOverlay !== null;
	// Match About / post-guarantee CTAs: Book a Call while LTO overlay is visible.
	const showBookACall = offerOverlay !== null;
	const countdownStartsAtMs = service.countdownOffer?.startsAt
		? new Date(service.countdownOffer.startsAt).getTime()
		: null;
	const countdownEndsAtMs = service.countdownOffer?.endsAt
		? new Date(service.countdownOffer.endsAt).getTime()
		: null;
	const countdownUsesPromotionalEnd =
		service.countdownOffer?.countdownUsesPromotionalEnd === true;
	const countdownDurationSeconds = (() => {
		if (
			countdownUsesPromotionalEnd &&
			countdownStartsAtMs !== null &&
			countdownEndsAtMs !== null &&
			!Number.isNaN(countdownStartsAtMs) &&
			!Number.isNaN(countdownEndsAtMs) &&
			countdownEndsAtMs > countdownStartsAtMs
		) {
			return Math.max(1, Math.floor((countdownEndsAtMs - countdownStartsAtMs) / 1000));
		}
		const fromField =
			typeof service.countdownOffer?.durationSeconds === "number" &&
			Number.isFinite(service.countdownOffer.durationSeconds)
				? Math.floor(service.countdownOffer.durationSeconds)
				: 0;
		if (fromField > 0) return fromField;
		// Legacy documents saved before durationSeconds existed: duration was implied by start→end.
		if (
			countdownStartsAtMs !== null &&
			countdownEndsAtMs !== null &&
			!Number.isNaN(countdownStartsAtMs) &&
			!Number.isNaN(countdownEndsAtMs) &&
			countdownEndsAtMs > countdownStartsAtMs
		) {
			return Math.max(1, Math.floor((countdownEndsAtMs - countdownStartsAtMs) / 1000));
		}
		return 0;
	})();
	const nowMs = Date.now();
	const shouldShowCountdown =
		countdownEnabled &&
		countdownStartsAtMs !== null &&
		countdownEndsAtMs !== null &&
		!Number.isNaN(countdownStartsAtMs) &&
		!Number.isNaN(countdownEndsAtMs) &&
		nowMs >= countdownStartsAtMs &&
		nowMs < countdownEndsAtMs &&
		isLimitedOfferExperienceActive;
	useEffect(() => {
		if (defaultPackageId.length === 0) {
			if (selectedPackageIdForTitle !== "") setSelectedPackageIdForTitle("");
			return;
		}
		const selectedPackageExists = service.packages.some((pkg) => pkg._id === selectedPackageIdForTitle);
		if (!selectedPackageExists) setSelectedPackageIdForTitle(defaultPackageId);
	}, [defaultPackageId, selectedPackageIdForTitle, service.packages]);
	const selectedPackageForTitle =
		service.packages.find((pkg) => pkg._id === selectedPackageIdForTitle) ?? service.packages[0];
	const selectedDeliveryDays = Number(selectedPackageForTitle?.dueDays);
	const deliverySuffixDays =
		offerOverlay?.variant === "active" &&
		Number.isFinite(selectedDeliveryDays) &&
		selectedDeliveryDays > 0
			? Math.round(selectedDeliveryDays)
			: null;
	const offerDiscountPercent =
		shouldShowCountdown &&
		typeof service.countdownOffer?.discountPercent === "number" &&
		Number.isFinite(service.countdownOffer.discountPercent) &&
		service.countdownOffer.discountPercent > 0
			? Math.min(100, Math.max(0, Math.round(service.countdownOffer.discountPercent)))
			: undefined;
	const showFreeTrialCta =
		offerOverlay?.variant === "active" && service.countdownOffer?.showFreeTrial === true;
	/** Hero buy CTA: launch-offer copy only while the personal countdown is still running. */
	const heroLaunchOfferPercent =
		offerOverlay?.variant === "active" &&
		typeof service.countdownOffer?.discountPercent === "number" &&
		Number.isFinite(service.countdownOffer.discountPercent) &&
		service.countdownOffer.discountPercent > 0
			? Math.min(100, Math.max(0, Math.round(service.countdownOffer.discountPercent)))
			: undefined;
	const heroPrimaryCta = showFreeTrialCta
		? {
				id: "start_14_day_free_trial_hero" as const,
				name: "Try it free for 14 days",
			}
		: heroLaunchOfferPercent !== undefined
			? {
					id: "get_launch_offer_hero" as const,
					name: `Get your launch offer (${heroLaunchOfferPercent}% off)`,
				}
			: {
					id: "buy_now_hero" as const,
					name: "Place your order",
				};
	const normalizedDemoUrl =
		typeof displayService.demoUrl === "string" && displayService.demoUrl.trim().length > 0
			? /^(https?:)?\/\//i.test(displayService.demoUrl.trim())
				? displayService.demoUrl.trim()
				: `https://${displayService.demoUrl.trim()}`
			: null;
	const normalizedDemoVideoUrl =
		typeof service.demoVideoUrl === "string" && service.demoVideoUrl.trim().length > 0
			? /^(https?:)?\/\//i.test(service.demoVideoUrl.trim())
				? service.demoVideoUrl.trim()
				: `https://${service.demoVideoUrl.trim()}`
			: null;
	const demoVideoYouTubeId = normalizedDemoVideoUrl
		? extractYouTubeId(normalizedDemoVideoUrl)
		: null;
	const demoVideoEmbedUrl =
		demoVideoYouTubeId !== null
			? getYouTubeEmbedUrl(demoVideoYouTubeId)
			: normalizedDemoVideoUrl;
	const demoLoginDetails = service.demoLoginDetails?.trim();
	const detailsHtml = displayService.detailsHtml?.trim();
	const escapeHtml = (value: string) =>
		value
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;");
	const applyBoldMarkdown = (value: string) =>
		value.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
	const formattedDemoLoginDetails = demoLoginDetails
		? sanitizeAllowedHtml(
				demoLoginDetails
					.split(/\n|\|/g)
					.map((rawLine) => rawLine.trim())
					.filter((line) => line.length > 0)
					.map((line) => applyBoldMarkdown(escapeHtml(line)))
					.join("<br />"),
				{ allowedTags: ["strong", "br"], allowedAttributes: [] },
			)
		: "";
	const parsedDetailsHtml =
		detailsHtml && detailsHtml.startsWith('"')
			? (() => {
					try {
						return JSON.parse(detailsHtml) as string;
					} catch {
						return detailsHtml;
					}
				})()
			: detailsHtml;

	let sanitizedDetailsHtml = "";
	if (parsedDetailsHtml) {
		try {
			sanitizedDetailsHtml = sanitizeAllowedHtml(parsedDetailsHtml, {
				// Envato / ThemeForest body HTML: lists, icon bullets (<i>), tables, semantic headings.
				allowedTags: [
					"p",
					"br",
					"strong",
					"em",
					"u",
					"b",
					"i",
					"h1",
					"h2",
					"h3",
					"h4",
					"h5",
					"h6",
					"ul",
					"ol",
					"li",
					"a",
					"img",
					"picture",
					"source",
					"blockquote",
					"code",
					"pre",
					"span",
					"div",
					"hr",
					"table",
					"thead",
					"tbody",
					"tfoot",
					"colgroup",
					"col",
					"tr",
					"th",
					"td",
					"caption",
					"dl",
					"dt",
					"dd",
					"sup",
					"sub",
				],
				allowedAttributes: [
					"href",
					"src",
					"alt",
					"title",
					"class",
					"id",
					"colspan",
					"rowspan",
					"scope",
					"headers",
					"abbr",
					"target",
					"rel",
					"srcset",
					"sizes",
					"type",
					"media",
					"width",
					"height",
					"loading",
					"decoding",
				],
				allowedSchemesByTag: {
					img: ["http", "https", "data"],
					source: ["http", "https", "data"],
				},
			});
			sanitizedDetailsHtml = stripLeadingEmptyDetailsHtml(sanitizedDetailsHtml);
		} catch {
			sanitizedDetailsHtml = "";
		}
	}
	const serviceContextDetails = toPlainContextText(
		sanitizedDetailsHtml || parsedDetailsHtml,
		3000,
	);
	const serviceContextPackages = service.packages
		.map((pkg) => {
			const price =
				formatAmounts(pkg.amounts) ||
				(typeof pkg.price === "number" ? `${pkg.currency ?? "USD"} ${pkg.price}` : "");
			return [
				pkg.title,
				pkg.description,
				price ? `Price: ${price}` : "",
				typeof pkg.dueDays === "number"
					? `Delivery: ${pkg.dueDays} day${pkg.dueDays === 1 ? "" : "s"}`
					: "",
				pkg.attributes
					? `Attributes: ${Object.entries(pkg.attributes)
							.map(([key, value]) => `${key}: ${value}`)
							.join(", ")}`
					: "",
			]
				.filter(Boolean)
				.join(" - ")
				.slice(0, 500);
		})
		.filter(Boolean);
	const serviceContextAddons = (service.addons ?? [])
		.map((addon) => {
			const price =
				formatAmounts(addon.amounts) ||
				(typeof addon.amount === "number" ? `Amount: ${addon.amount}` : "");
			return [
				addon.description,
				price,
				typeof addon.limit === "number"
					? `Limit: ${addon.limit}${addon.unit ? ` ${addon.unit}` : ""}`
					: "",
				addon.inputType ? `Input: ${addon.inputType}` : "",
			]
				.filter(Boolean)
				.join(" - ")
				.slice(0, 500);
		})
		.filter(Boolean);
	const serviceContextLinks = Array.from(
		new Set(
			[
				service.thumbnailImage ? `Thumbnail image: ${service.thumbnailImage}` : "",
				...service.media
					.map((item, index) => {
						const url = item.displayUrl || item.url;
						return url ? `Media ${index + 1} (${item.type || "media"}): ${url}` : "";
					})
					.filter(Boolean),
			].filter(Boolean),
		),
	).slice(0, 12);
	const triggerServiceAction = (buttonId: "add-to-basket" | "checkout") => {
		if (typeof window === "undefined") return;
		const root = document.getElementById("service-package-card");
		const target = root?.querySelector<HTMLButtonElement>(`button#${buttonId}`);
		target?.click();
	};

	useEffect(() => {
		setIsMounted(true);
	}, []);
	const scrollToPaymentOptions = () => {
		if (typeof window === "undefined") return;
		const target =
			document.getElementById("service-payment-options") ??
			document.getElementById("service-package-card");
		if (!target) return;
		const headerOffsetPx = 120;
		const y = target.getBoundingClientRect().top + window.scrollY - headerOffsetPx;
		window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
	};

	const openSupportChat = () => {
		window.dispatchEvent(
			new CustomEvent("techxeed-open-support-chat", { detail: { source: "get_in_touch" } }),
		);
	};

	return (
		<>
			{embedded ? <ServicePageEmbedBridge /> : null}
			<div className="hero-gradient-background hero-gradient-background-compact relative -mt-[90px] w-full overflow-x-hidden pt-[90px]">
				<div className="container relative z-10 px-0 pb-3 pt-3 sm:pb-4 sm:pt-4 lg:px-8 lg:pb-4 lg:pt-5">
					<div className="space-y-4 px-[15px] text-center sm:space-y-5 sm:px-8 lg:px-0">
						{/* Keep pill/title aligned whether or not the LTO timer is shown. */}
						<div
							className="mt-1 flex min-h-[88px] items-start justify-center sm:mt-2 sm:min-h-[96px]"
							aria-hidden={!(shouldShowCountdown && countdownDurationSeconds > 0)}
						>
							{shouldShowCountdown && countdownDurationSeconds > 0 ? (
								<ServiceCountdownBanner
									serviceId={service._id}
									durationSeconds={countdownDurationSeconds}
									promotionStartsAtMs={countdownStartsAtMs ?? undefined}
									promotionEndsAtMs={countdownEndsAtMs ?? undefined}
									usePromotionalEnd={countdownUsesPromotionalEnd}
									progressStartOffsetSeconds={
										countdownUsesPromotionalEnd
											? 0
											: typeof service.countdownOffer?.progressStartOffsetSeconds === "number"
												? service.countdownOffer.progressStartOffsetSeconds
												: undefined
									}
									discountPercent={offerDiscountPercent}
								/>
							) : null}
						</div>
						{showEyebrowPill && (
							<div className="mb-2 flex justify-center sm:mb-3">
								<div className="inline-flex rounded-full bg-gradient-to-r from-secondary to-tertiary p-[1px]">
									<p className="inline-flex max-w-full min-w-0 items-center rounded-full bg-transparent px-3 py-1 text-sm font-medium uppercase leading-none tracking-[0.08em] text-white sm:px-4 sm:py-1.5 sm:text-[15px]">
										{eyebrowPillText}
									</p>
								</div>
							</div>
						)}
						<div className="flex w-full flex-row items-center justify-center gap-2 sm:gap-4 md:gap-6 lg:gap-8">
							<Image
								src="/Leaf_left.svg"
								alt=""
								width={140}
								height={250}
								className="hidden h-20 w-auto max-w-[22%] shrink-0 object-contain object-right sm:block sm:h-28 md:h-36 lg:h-40"
							/>
							<h1 className="min-w-0 flex-1 text-[32px] font-semibold uppercase leading-none sm:text-[40px] lg:text-[50px]">
								{servicePageTitleWithPrefix(service.title, {
									quoteMiddle: deliverySuffixDays !== null,
								})}
								{deliverySuffixDays !== null ? (
									<span>
										{" "}
										within {deliverySuffixDays} day{deliverySuffixDays === 1 ? "" : "s"}
									</span>
								) : null}
							</h1>
							<Image
								src="/leaf_right.svg"
								alt=""
								width={140}
								height={250}
								className="hidden h-20 w-auto max-w-[22%] shrink-0 object-contain object-left sm:block sm:h-28 md:h-36 lg:h-40"
							/>
						</div>
						<p className="mx-auto max-w-6xl px-2 text-base text-grey-foreground sm:px-4 sm:text-lg lg:text-[20px]">
							{service.description}
						</p>
						<ul className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-5 gap-y-2 px-2 sm:gap-x-6">
							{[
								"Easy to Maintain",
								"SEO Friendly",
								"Hosting Included",
								"Maintenance Support",
							].map((label) => (
								<li
									key={label}
									className="inline-flex items-center gap-1.5 text-sm text-grey-foreground sm:text-base"
								>
									<Image
										src="/assets/profile/tick_premium.png"
										alt=""
										width={20}
										height={20}
										className="h-5 w-5 shrink-0"
									/>
									{label}
								</li>
							))}
						</ul>
						<div className="mx-auto flex w-full max-w-[480px] flex-col items-center">
							<div className="flex w-full flex-row items-center justify-center gap-2 py-3 sm:gap-4">
								<TrackedCtaButton
									type="button"
									ctaId={showBookACall ? "book_a_call_hero" : "get_in_touch_hero"}
									ctaName={showBookACall ? "Book a call" : "Get in Touch"}
									ctaLocation="service_hero"
									onClick={() => {
										if (showBookACall) {
											setBookCallOpen(true);
											return;
										}
										openSupportChat();
									}}
									className="h-11 min-w-0 flex-1 basis-0 whitespace-nowrap rounded-full bg-secondary px-2 text-[clamp(0.625rem,2.5vw,0.75rem)] font-semibold leading-none text-secondary-foreground shadow-md hover:bg-secondary/90 sm:h-12 sm:min-w-[265px] sm:flex-none sm:px-8 sm:text-sm"
								>
									{showBookACall ? "Book a call" : "Get in Touch"}
								</TrackedCtaButton>
								<TrackedCtaButton
									type="button"
									ctaId={heroPrimaryCta.id}
									ctaName={heroPrimaryCta.name}
									ctaLocation="service_hero"
									onClick={scrollToPaymentOptions}
									className="h-11 min-w-0 flex-[1.35] basis-0 whitespace-nowrap rounded-full bg-tertiary px-1.5 text-[clamp(0.5625rem,2.3vw,0.75rem)] font-semibold leading-none text-white shadow-md hover:bg-tertiary/90 sm:h-12 sm:min-w-[265px] sm:flex-none sm:px-8 sm:text-sm"
								>
									{heroPrimaryCta.name}
								</TrackedCtaButton>
							</div>
							{offerOverlay !== null ? (
								<p className="mt-1.5 max-w-xl px-2 text-center text-[10px] leading-snug text-muted-foreground sm:mt-2 sm:text-[10px]">
Subject to availability. Due to high order volume, we limit monthly order
								intake. Once capacity is reached, new customers can join our waitlist.
								</p>
							) : null}
						</div>
						<ServiceReviewSummaryStrip variant="compact" ctaLocation="service_hero_review_summary" />
					</div>
				</div>
			</div>

			{!embedded && designVariants.length > 0 ? (
				<div className="container overflow-x-hidden px-0 pt-2 lg:px-8">
					<ServiceDesignPicker
						landingService={service}
						variants={designVariants}
						selectedDesignId={preferredDesignId}
						onSelect={setPreferredDesignId}
					/>
				</div>
			) : null}

			<div className="container overflow-x-hidden px-0 pb-8 lg:px-8 lg:pb-8 lg:pt-0">
				<div
					id="service-package-section"
					className="mt-1 grid grid-cols-1 gap-6 sm:mt-1 lg:mt-0 lg:grid-cols-[minmax(0,1fr)_26rem] lg:items-start lg:gap-8"
				>
					<div className="order-1 flex min-w-0 flex-col lg:col-start-1 lg:row-start-1">
						<div
							id="service-media-section"
							className="w-full scroll-mt-[7.5rem] px-[15px] sm:px-8 lg:px-0"
						>
							<ServiceMediaCarousel
								session={session}
								service={displayService}
								designSelectionKey={preferredDesignId}
								liveDemoFullscreen={
									normalizedDemoUrl != null &&
									isSafeHttpOrHttpsUrl(normalizedDemoUrl)
										? {
												loginDetailsHtml:
													formattedDemoLoginDetails || undefined,
											}
										: undefined
								}
							/>
							<ServiceCardSubcategoryPills
								service={service}
								variant="servicePage"
								className="mt-5"
								fadeEdgeClassName="from-background via-background/85 to-transparent dark:from-background dark:via-background/85"
							/>
							<ServiceHowItWorksSection />
						</div>

						{isMounted &&
							demoVideoEmbedUrl != null &&
							isSafeHttpOrHttpsUrl(demoVideoEmbedUrl) && (
								<div className="flex items-center justify-center px-[15px] sm:px-8 lg:px-0">
									<Dialog>
										<DialogTrigger asChild>
											<Button
												variant="outline"
												className="mt-5 h-8 w-40 gap-2 self-center rounded-none border-secondary font-light text-secondary hover:border-tertiary hover:bg-transparent"
											>
												<PlayCircle className="size-4" />
												Demo Video
											</Button>
										</DialogTrigger>
										<DialogContent
											className="h-[90vh] w-[95vw] max-w-[1200px] p-0 overflow-visible"
											hideClose
										>
											<DialogTitle className="sr-only">
												Demo Video
											</DialogTitle>
											<DialogDescription className="sr-only">
												Embedded demo video preview
											</DialogDescription>
											<DialogClose className="absolute right-0 -top-9 inline-flex size-8 items-center justify-center text-secondary transition-colors hover:text-tertiary sm:-right-8 sm:-top-8 sm:size-10">
												<X className="size-6" />
												<span className="sr-only">Close</span>
											</DialogClose>
											<div
												className="no-media-save h-full w-full overflow-hidden rounded-md bg-black"
												onContextMenu={(event) => event.preventDefault()}
											>
												{demoVideoYouTubeId ? (
													<iframe
														src={demoVideoEmbedUrl!}
														title="Service demo video"
														className="h-full w-full border-0"
														loading="lazy"
														allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
														referrerPolicy="strict-origin-when-cross-origin"
														allowFullScreen
													/>
												) : (
													<video
														src={demoVideoEmbedUrl!}
														className="h-full w-full"
														controls
														controlsList="nodownload noplaybackrate noremoteplayback"
														disablePictureInPicture
														disableRemotePlayback
														draggable={false}
														playsInline
														preload="metadata"
														onContextMenu={(event) => event.preventDefault()}
													/>
												)}
											</div>
										</DialogContent>
									</Dialog>
								</div>
							)}
						<div className="hidden min-w-0 flex-col gap-8 pt-8 lg:flex">
							{detailsHtml ? (
								<ServiceDeferredBlock placeholder={<ServiceBlockPlaceholder className="h-80" />}>
									<DeferredServiceDetailsExpandableDetails
										html={sanitizedDetailsHtml}
										className={styles["details-rich-text"]}
										ctaLocation="service_details_desktop"
									/>
								</ServiceDeferredBlock>
							) : null}

							{!embedded ? (
								<ServiceDeferredBlock placeholder={<ServiceBlockPlaceholder className="h-72" />}>
									<DeferredServiceFrequentlyBoughtTogetherSection
										services={activeFrequentlyBoughtTogether}
										currentServiceId={preferredDesignId ?? service._id}
									/>
								</ServiceDeferredBlock>
							) : null}
						</div>
					</div>

					<div
						id="service-package-card"
						className="service-package-card-sticky order-2 w-full px-[15px] sm:px-8 lg:col-start-2 lg:row-start-1 lg:w-[26rem] lg:shrink-0 lg:px-0 lg:sticky lg:self-start lg:justify-self-start"
					>
						<PackageSection
							session={session}
							service={purchaseService}
							designPurchasePending={designPurchasePending}
							addToBasketButtonId={
								embedded ? "fbt-embed-add-to-basket" : "add-to-basket"
							}
							onSelectedPackageChange={setSelectedPackageIdForTitle}
						/>
					</div>

					<div className="order-3 flex min-w-0 flex-col gap-8 px-[15px] sm:px-8 lg:hidden">
						{detailsHtml ? (
							<ServiceDeferredBlock placeholder={<ServiceBlockPlaceholder className="h-80" />}>
								<DeferredServiceDetailsExpandableDetails
									html={sanitizedDetailsHtml}
									className={styles["details-rich-text"]}
									ctaLocation="service_details_mobile"
								/>
							</ServiceDeferredBlock>
						) : null}

						{!embedded ? (
							<ServiceDeferredBlock placeholder={<ServiceBlockPlaceholder className="h-72" />}>
								<DeferredServiceFrequentlyBoughtTogetherSection
									services={activeFrequentlyBoughtTogether}
									currentServiceId={preferredDesignId ?? service._id}
								/>
							</ServiceDeferredBlock>
						) : null}
					</div>
				</div>
			</div>

			{!embedded ? (
				<ServiceDeferredBlock
					placeholder={<ServiceBlockPlaceholder className="mx-auto h-96 max-w-6xl" />}
				>
					<DeferredServiceCompanyReviewsCarousel />
				</ServiceDeferredBlock>
			) : null}
			{!embedded ? (
				<ServiceDeferredBlock
					placeholder={<ServiceBlockPlaceholder className="mx-auto h-96 max-w-6xl" />}
				>
					<DeferredServiceVideoReviewsCarousel />
				</ServiceDeferredBlock>
			) : null}
			{!embedded ? (
				<ServiceDeferredBlock placeholder={<ServiceBlockPlaceholder className="container h-96" />}>
					<DeferredServiceAboutSection
						service={service}
						serviceTitle={formatServiceDisplayTitle(service.title)}
					/>
				</ServiceDeferredBlock>
			) : null}
			{!embedded ? (
				<ChatNowFAB
					pageContext={{
						type: "service",
						title: formatServiceDisplayTitle(service.title),
						categoryTitle:
							typeof service.categoryId === "object" && service.categoryId?.title
								? service.categoryId.title
								: undefined,
						sectorTitles: (service.subcategoryIds ?? [])
							.map((sub) => (typeof sub === "object" && sub?.title ? sub.title : ""))
							.filter(Boolean),
						listingRole: service.listingRole === "child" ? "child" : "parent",
						parentListingTitle:
							service.listingRole === "child" &&
							typeof service.parentListingServiceId === "object" &&
							service.parentListingServiceId?.title
								? service.parentListingServiceId.title
								: undefined,
						excerpt: service.excerpt?.trim() || undefined,
						description: service.description?.trim() || undefined,
						details: serviceContextDetails,
						aiContextSummary: trimLauraGatherBrief(service.listingAiContextSummary),
						packages: serviceContextPackages,
						addons: serviceContextAddons,
						links: serviceContextLinks,
						highlights: service.packages.map((pkg) => pkg.title).filter(Boolean),
					}}
				/>
			) : null}
			{!embedded ? (
				<BookCallDialog
					open={bookCallOpen}
					onOpenChange={setBookCallOpen}
					serviceTitle={formatServiceDisplayTitle(service.title)}
				/>
			) : null}
			{!embedded &&
			offerOverlay?.variant === "active" &&
			(service.countdownOffer?.socialProofUk !== false ||
				service.countdownOffer?.socialProofUs === true) ? (
				<LtoSocialProofBanner
					serviceId={service._id}
					active
					audience={{
						uk: service.countdownOffer?.socialProofUk !== false,
						us: service.countdownOffer?.socialProofUs === true,
					}}
				/>
			) : null}
			{!embedded &&
			offerOverlay?.variant === "active" &&
			service.countdownOffer?.showNewsletterOffer !== false ? (
				<LtoOfferSubscribePopup
					serviceId={service._id}
					countdownActive
				/>
			) : null}
		</>
	);
}
